import { RG_TYPE, getRgObjectsOfType } from "@project-rouge/rg-core";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { when } from "mobx";
import { useCurrencySymbol } from "src/hooks/useCurrencySymbol";
import { Tooltip } from "src/components/Tooltip";
import { COST_TRACKING_VARIANTS } from "src/screens/cost-tracking-variants";
import type { CostingViewSummary } from "src/service/cost/utility/custom-view";
import type { ScenarioConfiguration } from "src/service/cost/data/building";
import type { ArchitecturalMetricsSnapshot } from "src/types/ArchitecturalMetrics";
import { DesignsGenerator } from "src/types/DesignsGenerator";
import type { GeneratedItem } from "src/types/GeneratedItem";
import type { RgBuilding } from "src/types/RgCorePackage";
import { CreateBoxWorld } from "src/utils/CreateBoxWorld";
import { GetCostDataHash } from "src/utils/GetCostDataHash";
import { RgWorldFromZip } from "src/utils/RgWorldFromZip";
import { RgWorldToBeCostPayload } from "src/utils/RgWorldToBeCostPayload";
import {
  computeGerman,
  computeGla,
  fmt,
  fmtRate,
  GERMAN_DEFAULTS,
  pct,
  type BkiInput,
  type GermanCalc,
  type KgResult,
  type Resolved,
  type WflOption,
} from "src/screens/cost-engine-compare-engine";
import {
  BKI_CITY_LIST,
  type BkiCity,
  type BkiSizeBracket,
  type BkiStandard,
} from "src/service/cost/data/bki";

// Side-by-side comparison of the existing UK/RIBA cost engine and the
// proposed German DIN 276 engine for the same generated building variant.
// Flips between systems and blurs out fields the active engine doesn't
// consume, so stakeholders can see the input-contract delta.
//
// German engine logic is hard-coded — see ./cost-engine-compare-engine.ts.
// Manual overrides are ephemeral per page-load — refresh resets them.

type System = "uk" | "german";
type ViewMode = "dev" | "presentable";
type EditMode = "read" | "manual";

export const CostEngineCompareScreen = observer(function CostEngineCompareScreen() {
  const { variantIndex } = useParams<{ variantIndex: string }>();
  const idx = Number(variantIndex);
  const variant = COST_TRACKING_VARIANTS[idx];
  const ukCurrency = useCurrencySymbol();

  const [system, setSystem] = useState<System>("uk");
  const [view, setView] = useState<ViewMode>("presentable");
  const [mode, setMode] = useState<EditMode>("read");
  const [wflOption, setWflOption] = useState<WflOption>("bgf");
  const [inflation, setInflation] = useState(1);
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  const [bkiCity, setBkiCity] = useState<BkiCity | null>(null);
  const [bkiSize, setBkiSize] = useState<BkiSizeBracket>(">19");
  const [bkiStandard, setBkiStandard] = useState<BkiStandard>("medium");

  const [item, setItem] = useState<GeneratedItem | null>(null);
  const [payload, setPayload] = useState<ScenarioConfiguration | null>(null);
  const [costDataHash, setCostDataHash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!variant) return;
    setItem(null);
    setPayload(null);
    setError(null);
    setOverrides({});
    let cancelled = false;
    const generator = new DesignsGenerator({
      projectId: "cost-engine-compare",
      listSize: 1,
    });
    (async () => {
      try {
        const world = CreateBoxWorld(variant.length, variant.width, variant.floors);
        generator.generate(world);
        await new Promise<void>((resolve) =>
          when(
            () => !generator.isRunning || generator.list.length > 0,
            () => resolve()
          )
        );
        if (cancelled) return;
        const generated = generator.list[0];
        if (!generated) {
          setError("Generator produced no option");
          return;
        }
        setItem(generated);
        const rgWorld = RgWorldFromZip(generated.rgWorldZip);
        const buildings = getRgObjectsOfType<RgBuilding>(rgWorld, RG_TYPE.Building);
        setPayload(RgWorldToBeCostPayload(rgWorld, buildings));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    GetCostDataHash()
      .then((h) => {
        if (!cancelled) setCostDataHash(h);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
      generator.terminate();
    };
  }, [variant]);

  const arch = item?.architecturalMetrics ?? null;
  const ukSummary = item?.costMetrics?.summary ?? null;
  const bkiInput: BkiInput | null = bkiCity
    ? { city: bkiCity, sizeBracket: bkiSize, standard: bkiStandard }
    : null;
  const germanCalc = useMemo(
    () => (arch ? computeGerman(arch, wflOption, overrides, bkiInput, inflation) : null),
    [arch, wflOption, overrides, bkiInput, inflation]
  );

  const setOverride = (key: string, value: number | null) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (value == null || Number.isNaN(value)) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  if (!variant) {
    return (
      <div className="p-6 font-mono text-xs">
        <p className="mb-2">Unknown variant index: {variantIndex}</p>
        <Link to="/admin/cost-tracking" className="text-neutral-6 hover:text-neutral-8 underline">
          ← back to cost tracking
        </Link>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-y-auto bg-white text-neutral-8 font-mono text-xs">
      <header className="sticky top-0 bg-white border-b border-neutral-3 px-6 py-3 flex items-baseline gap-4 z-10">
        <Link
          to={`/admin/cost-tracking/${idx}`}
          className="text-neutral-6 hover:text-neutral-8 shrink-0 underline-offset-2 hover:underline"
        >
          ← back to detail
        </Link>
        <h1 className="text-base font-bold">Cost engine compare · {variant.label}</h1>
        <span className="text-neutral-6 shrink-0">
          {variant.length}×{variant.width}m · {variant.floors} floors
        </span>
        <span className="ml-auto text-neutral-6">
          cost_data hash: <code className="text-neutral-8">{costDataHash ?? "…"}</code>
        </span>
      </header>

      <Toolbar
        system={system}
        view={view}
        mode={mode}
        wflOption={wflOption}
        inflation={inflation}
        bkiCity={bkiCity}
        bkiSize={bkiSize}
        bkiStandard={bkiStandard}
        setSystem={setSystem}
        setView={setView}
        setMode={setMode}
        setWflOption={setWflOption}
        setInflation={setInflation}
        setBkiCity={setBkiCity}
        setBkiSize={setBkiSize}
        setBkiStandard={setBkiStandard}
      />

      {error && <p className="px-6 py-2 text-danger-7">Error: {error}</p>}

      <main className="grid grid-cols-3 gap-3 p-3">
        <InputsColumn
          system={system}
          view={view}
          mode={mode}
          wflOption={wflOption}
          payload={payload}
          arch={arch}
          germanCalc={germanCalc}
          overrides={overrides}
          setOverride={setOverride}
        />
        <EquationsColumn
          system={system}
          view={view}
          ukSummary={ukSummary}
          germanCalc={germanCalc}
        />
        <OutputsColumn
          system={system}
          ukSummary={ukSummary}
          ukCurrency={ukCurrency}
          germanCalc={germanCalc}
        />
      </main>
    </div>
  );
});

// ─── Toolbar ──────────────────────────────────────────────────────────────

function Toolbar(props: {
  system: System;
  view: ViewMode;
  mode: EditMode;
  wflOption: WflOption;
  inflation: number;
  bkiCity: BkiCity | null;
  bkiSize: BkiSizeBracket;
  bkiStandard: BkiStandard;
  setSystem: (v: System) => void;
  setView: (v: ViewMode) => void;
  setMode: (v: EditMode) => void;
  setWflOption: (v: WflOption) => void;
  setInflation: (v: number) => void;
  setBkiCity: (v: BkiCity | null) => void;
  setBkiSize: (v: BkiSizeBracket) => void;
  setBkiStandard: (v: BkiStandard) => void;
}) {
  const germanActive = props.system === "german";
  // Local text mirror so the user can type "1." / "1.05" freely; the parent
  // only ever receives a valid positive number (empty/invalid falls back to 1).
  const [inflationText, setInflationText] = useState(String(props.inflation));
  return (
    <div className="border-b border-neutral-3 px-6 py-2 flex flex-wrap items-center gap-6 bg-neutral-1">
      <ToggleGroup
        label="System"
        value={props.system}
        options={[
          { value: "uk", label: "UK · RIBA" },
          { value: "german", label: "German · DIN 276" },
        ]}
        onChange={props.setSystem}
      />
      <ToggleGroup
        label="View"
        value={props.view}
        options={[
          { value: "presentable", label: "Presentable" },
          { value: "dev", label: "Dev" },
        ]}
        onChange={props.setView}
      />
      <ToggleGroup
        label="Mode"
        value={props.mode}
        options={[
          { value: "read", label: "Read-only" },
          { value: "manual", label: "Manual override" },
        ]}
        onChange={props.setMode}
      />
      <div className={`flex items-center gap-1 ${germanActive ? "" : "opacity-30"}`}>
        <span className="text-neutral-6 text-[10px] uppercase tracking-wide mr-1">Ref area</span>
        {(["bgf", "strict", "nia", "leasable"] as WflOption[]).map((opt) => (
          <button
            key={opt}
            type="button"
            disabled={!germanActive}
            onClick={() => props.setWflOption(opt)}
            className={togglePillClass(props.wflOption === opt)}
            title={
              opt === "bgf"
                ? "BGF / GEA (gross floor area) — what BKI rates are quoted against"
                : "Wfl (Wohnfläche) derivation — fallback"
            }
          >
            {opt}
          </button>
        ))}
      </div>
      <div className={`flex items-center gap-1 ${germanActive ? "" : "opacity-30"}`}>
        <span className="text-neutral-6 text-[10px] uppercase tracking-wide mr-1">Inflation</span>
        <input
          type="number"
          step="0.01"
          min="0"
          disabled={!germanActive}
          value={inflationText}
          onChange={(e) => {
            const raw = e.target.value;
            setInflationText(raw);
            const n = Number(raw);
            props.setInflation(raw !== "" && Number.isFinite(n) && n > 0 ? n : 1);
          }}
          className="w-16 border border-neutral-3 rounded px-2 py-1 text-xs text-right disabled:opacity-50"
          title="Inflation factor on KG 200–600 (BKI-rated groups). Excludes land (KG 100) and capital (KG 800). Default 1.0."
        />
        <span className="text-neutral-5 text-[10px]">×</span>
      </div>
      <div className={`flex items-center gap-1 ${germanActive ? "" : "opacity-30"}`}>
        <span className="text-neutral-6 text-[10px] uppercase tracking-wide mr-1">BKI</span>
        <select
          disabled={!germanActive}
          value={props.bkiCity ?? ""}
          onChange={(e) =>
            props.setBkiCity(e.target.value ? (e.target.value as BkiCity) : null)
          }
          className="border border-neutral-3 rounded px-2 py-1 text-xs disabled:opacity-50"
        >
          <option value="">— none (illustrative)</option>
          {BKI_CITY_LIST.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        {(["<6", "6-19", ">19"] as BkiSizeBracket[]).map((opt) => (
          <button
            key={opt}
            type="button"
            disabled={!germanActive || !props.bkiCity}
            onClick={() => props.setBkiSize(opt)}
            className={togglePillClass(props.bkiSize === opt)}
            title="Building size: residential units"
          >
            {opt}
          </button>
        ))}
        {(["simple", "medium", "high"] as BkiStandard[]).map((opt) => (
          <button
            key={opt}
            type="button"
            disabled={!germanActive || !props.bkiCity}
            onClick={() => props.setBkiStandard(opt)}
            className={togglePillClass(props.bkiStandard === opt)}
            title="Standard of fit-out"
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function ToggleGroup<T extends string>(props: {
  label: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-neutral-6 text-[10px] uppercase tracking-wide mr-1">{props.label}</span>
      {props.options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => props.onChange(opt.value)}
          className={togglePillClass(props.value === opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function togglePillClass(active: boolean) {
  return `px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
    active ? "bg-neutral-8 text-white" : "bg-neutral-2 text-neutral-6 hover:bg-neutral-3"
  }`;
}

// ─── Inputs column ────────────────────────────────────────────────────────

function InputsColumn(props: {
  system: System;
  view: ViewMode;
  mode: EditMode;
  wflOption: WflOption;
  payload: ScenarioConfiguration | null;
  arch: ArchitecturalMetricsSnapshot | null;
  germanCalc: GermanCalc | null;
  overrides: Record<string, number>;
  setOverride: (key: string, value: number | null) => void;
}) {
  const ukSection = (
    <Section blurred={props.system !== "uk"} label="UK payload">
      {props.payload ? (
        props.view === "dev" ? (
          <DevJson value={props.payload} />
        ) : (
          <UkPresentable payload={props.payload} />
        )
      ) : (
        <p className="text-neutral-5 italic">Generating…</p>
      )}
    </Section>
  );
  const germanSection = (
    <Section blurred={props.system !== "german"} label="German inputs">
      {props.arch && props.germanCalc ? (
        props.view === "dev" ? (
          <DevJson value={germanInputJson(props.germanCalc)} />
        ) : (
          <GermanPresentable
            arch={props.arch}
            calc={props.germanCalc}
            wflOption={props.wflOption}
            mode={props.mode}
            overrides={props.overrides}
            setOverride={props.setOverride}
          />
        )
      ) : (
        <p className="text-neutral-5 italic">Generating…</p>
      )}
    </Section>
  );
  return (
    <Column title="Inputs" subtitle={props.system === "uk" ? "RgWorldToBeCostPayload" : "5 geometry + KG rates"}>
      {props.system === "uk" ? (
        <>
          {ukSection}
          {germanSection}
        </>
      ) : (
        <>
          {germanSection}
          {ukSection}
        </>
      )}
    </Column>
  );
}

function germanInputJson(calc: GermanCalc) {
  return { inputs: calc.inputs, rates: calc.rates };
}

function UkPresentable(props: { payload: ScenarioConfiguration }) {
  const { scenario, buildings } = props.payload;
  return (
    <div className="space-y-3">
      <div>
        <SectionLabel>Scenario</SectionLabel>
        <KvTable
          rows={[
            ["scenario_area (Plotsize)", `${fmt(scenario.scenario_area)} m²`],
            ["scenario_area_net (Plotsize × (1−SCR))", `${fmt(scenario.scenario_area_net)} m²`],
            ["scenario_perimeter", `${fmt(scenario.scenario_perimeter)} m`],
            ["number_of_buildings", String(scenario.number_of_buildings)],
          ]}
        />
      </div>
      {buildings.map((b, i) => (
        <div key={i}>
          <SectionLabel>Building {i + 1}</SectionLabel>
          <KvTable
            rows={Object.entries(b.data).map(([k, v]) => [
              k,
              typeof v === "number" ? fmt(v) : String(v),
            ])}
          />
          <SectionLabel>· units</SectionLabel>
          <KvTable rows={b.units.map((u) => [u.unit, String(u.quantity)])} />
        </div>
      ))}
    </div>
  );
}

function GermanPresentable(props: {
  arch: ArchitecturalMetricsSnapshot;
  calc: GermanCalc;
  wflOption: WflOption;
  mode: EditMode;
  overrides: Record<string, number>;
  setOverride: (key: string, value: number | null) => void;
}) {
  const { calc, mode } = props;
  return (
    <div className="space-y-3">
      <AssumptionBox calc={calc} wflOption={props.wflOption} />
      <div>
        <SectionLabel>Geometry (5 fields)</SectionLabel>
        <GeometryRow
          label="Plotsize"
          german="Grundstück"
          value={`${fmt(calc.inputs.plotsize)} m²`}
          source="DERIVED"
          tooltip="siteAreaHectares × 10000"
        />
        <GeometryRow
          label="GFA"
          german="BGF"
          value={`${fmt(calc.inputs.gfa)} m²`}
          source="HAVE · GEA"
          tooltip="Sum of building Gross External Area (incl. external walls)"
        />
        <GlaBlock
          arch={props.arch}
          calc={calc}
          wflOption={props.wflOption}
          mode={mode}
          overrides={props.overrides}
          setOverride={props.setOverride}
        />
        <GeometryRow
          label="SCR"
          german="GRZ"
          value={fmt(calc.inputs.scr, 3)}
          source="HAVE · LandCoverage"
          tooltip="footprint / Plotsize — already a ratio"
        />
        <GeometryRow
          label="FSR"
          german="GFZ"
          value={fmt(calc.inputs.fsr, 3)}
          source="DERIVED"
          tooltip="GFA / Plotsize"
          derived
        />
      </div>

      <div>
        <SectionLabel>Per-KG rates (€/m²)</SectionLabel>
        <RateRow
          kgKey="rate100"
          label="KG 100 · Site"
          unit="€/m²"
          resolved={calc.rates.rate100}
          mode={mode}
          overrides={props.overrides}
          setOverride={props.setOverride}
          tooltip="Land cost. Source: Bodenrichtwert (BRW) lookup or manual €/m². No default."
        />
        <RateRow
          kgKey="rate200"
          label="KG 200 · Clearance"
          unit="€/m²"
          resolved={calc.rates.rate200}
          mode={mode}
          overrides={props.overrides}
          setOverride={props.setOverride}
          tooltip="Site clearance & preparation. We provide a default €/m²; user can override."
        />
        <RateRow
          kgKey="rate300"
          label="KG 300 · Construction"
          unit="€/m²"
          resolved={calc.rates.rate300}
          mode={mode}
          overrides={props.overrides}
          setOverride={props.setOverride}
          tooltip="Building structure. Source: BKI / manufacturer. No default — must be sourced."
        />
        <RateRow
          kgKey="rate400"
          label="KG 400 · Technical"
          unit="€/m²"
          resolved={calc.rates.rate400}
          mode={mode}
          overrides={props.overrides}
          setOverride={props.setOverride}
          tooltip="MEP / services. Source: BKI / manufacturer. No default — must be sourced."
        />
        <RateRow
          kgKey="rate500"
          label="KG 500 · Landscaping"
          unit="€/m²"
          resolved={calc.rates.rate500}
          mode={mode}
          overrides={props.overrides}
          setOverride={props.setOverride}
          tooltip="External works / landscaping. We provide a default €/m²; user can override."
        />
      </div>

      <div>
        <SectionLabel>Lumps & quotes</SectionLabel>
        <RateRow
          kgKey="lump600"
          label="KG 600 · Furnishing"
          unit="€"
          resolved={calc.rates.lump600}
          mode={mode}
          overrides={props.overrides}
          setOverride={props.setOverride}
          tooltip="Furnishing budget. User-provided lump sum."
        />
        <RateRow
          kgKey="quote800"
          label="KG 800 · Financing"
          unit="€"
          resolved={calc.rates.quote800}
          mode={mode}
          overrides={props.overrides}
          setOverride={props.setOverride}
          tooltip="Capital cost. Source: capital provider quote / API. No default."
        />
      </div>

      <div>
        <SectionLabel>Percentages</SectionLabel>
        <RateRow
          kgKey="pct700"
          label="KG 700 · Incidentals"
          unit="%"
          isPct
          resolved={calc.rates.pct700}
          mode={mode}
          overrides={props.overrides}
          setOverride={props.setOverride}
          tooltip="% of Σ(KG 200..500). Manufacturer default; overridable."
        />
        <RateRow
          kgKey="pctContingency"
          label="Contingencies"
          unit="%"
          isPct
          resolved={calc.rates.pctContingency}
          mode={mode}
          overrides={props.overrides}
          setOverride={props.setOverride}
          tooltip="% of Σ(KG 200..500). Default provided."
        />
        <RateRow
          kgKey="pctOhp"
          label="Overhead + Profit"
          unit="%"
          isPct
          resolved={calc.rates.pctOhp}
          mode={mode}
          overrides={props.overrides}
          setOverride={props.setOverride}
          tooltip="% of Σ(KG 200..500). Default provided."
        />
      </div>
    </div>
  );
}

// Surfaces the modelling assumptions baked into the German engine, in the same
// amber style as the WFL warning below. Adapts its wording to the current
// reference-area choice and whether the resi/commercial split is live.
function AssumptionBox(props: { calc: GermanCalc; wflOption: WflOption }) {
  const { calc, wflOption } = props;
  const comm = calc.inputs.commercialArea;
  const bkiActive = [
    calc.rates.rate200,
    calc.rates.rate300,
    calc.rates.rate400,
    calc.rates.rate500,
  ].some((r) => r.source.startsWith("BKI"));
  const splitActive = bkiActive && comm > 0;
  const onBgf = wflOption === "bgf";
  return (
    <div className="border-l-2 border-warning-7 pl-2 py-1.5 bg-warning-1/40 rounded-r text-[10px] leading-snug text-neutral-7 space-y-1">
      <p className="uppercase tracking-wide text-warning-7 font-semibold text-[9px]">Assumptions</p>
      <p>
        <strong>Reference area:</strong> KG 300/400 are calculated on{" "}
        {onBgf ? (
          <strong>BGF (GEA)</strong>
        ) : (
          <>
            a Wfl derivation (<code>{wflOption}</code>)
          </>
        )}{" "}
        — BGF is what BKI rates are quoted against. Switch <em>Ref area</em> to change.
      </p>
      <p>
        <strong>Commercial split:</strong> commercial floorspace uses separate KG 300/400 rates
        (€1,912 / €476 per m²); all other groups use the residential rates. Resi area ={" "}
        {onBgf ? "BGF" : "ref area"} − commercial.{" "}
        {splitActive
          ? `Active here: ${fmt(comm)} m² commercial.`
          : comm > 0
          ? `Inactive — needs a BKI city for commercial rates (${fmt(comm)} m² commercial).`
          : "Inactive — this building has no commercial area."}
      </p>
      <p>
        <strong>Inflation:</strong> ×{calc.inflationFactor} applied to KG 200–600; land (KG 100)
        and capital (KG 800) are excluded.
      </p>
    </div>
  );
}

function GlaBlock(props: {
  arch: ArchitecturalMetricsSnapshot;
  calc: GermanCalc;
  wflOption: WflOption;
  mode: EditMode;
  overrides: Record<string, number>;
  setOverride: (key: string, value: number | null) => void;
}) {
  const { arch, calc, wflOption, mode, overrides, setOverride } = props;
  const isDerived = calc.inputs.gla.source.startsWith("DERIVED");
  const isManual = mode === "manual";

  // Compact header: label + value (or input in manual mode) + source badge.
  const onBgf = wflOption === "bgf";
  const header = (
    <div className="flex items-baseline gap-2">
      <Tooltip.Trigger
        text={
          onBgf
            ? "BGF / GEA (gross floor area, incl. external walls) — a real Tessa field, the basis BKI rates are quoted against. No WoFlV derivation needed."
            : "German WFL (Wohnfläche) has no exact match in Tessa. Tessa stores GIA / NIA / GEA — German WFL is legally defined by the WoFlV ordinance (counts balconies at 25% etc.) which is not implemented. Pick a pragmatic derivation rule, or enter a real value in Manual mode."
        }
        className="shrink-0"
      >
        <span className="text-neutral-7">
          {onBgf ? (
            <>
              Reference area <span className="text-neutral-5">(BGF · GEA)</span>
            </>
          ) : (
            <>
              GLA <span className="text-neutral-5">(WFL · German Wohnfläche)</span>
            </>
          )}
        </span>
      </Tooltip.Trigger>
      <div className="ml-auto flex items-center gap-2">
        {isManual ? (
          <input
            type="number"
            step="any"
            aria-label="GLA override (m²)"
            placeholder={
              calc.inputs.gla.value != null ? String(Math.round(calc.inputs.gla.value)) : "—"
            }
            value={overrides.gla != null ? String(overrides.gla) : ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") return setOverride("gla", null);
              const n = Number(raw);
              if (Number.isNaN(n)) return;
              setOverride("gla", n);
            }}
            className={`w-24 px-1 py-0.5 border rounded font-mono text-right ${
              isDerived ? "border-warning-7 bg-warning-1" : "border-neutral-3"
            }`}
          />
        ) : (
          <span
            className={`font-mono font-semibold ${
              isDerived ? "text-warning-7" : "text-neutral-8"
            }`}
          >
            {calc.inputs.gla.value != null ? `${fmt(calc.inputs.gla.value)} m²` : "—"}
          </span>
        )}
        {!isManual && <span className="text-neutral-5 text-[11px]">m²</span>}
        <SourceBadge source={calc.inputs.gla.source} />
      </div>
    </div>
  );

  // When we have a "real" value (manual entered, or one day a HAVE source),
  // skip the explanatory block — render a clean row like Plotsize/GFA.
  if (!isDerived) {
    return <div className="py-0.5 text-[11px]">{header}</div>;
  }

  // Derived: show rich block with formula + meanings + comparison.
  const RULE_DEFS: Array<{
    rule: WflOption;
    title: string;
    meaning: string;
  }> = [
    {
      rule: "strict",
      title: "defensible read of spec",
      meaning: "GIA − cores+corridors (circulation) − services − amenities (ancillary)",
    },
    {
      rule: "nia",
      title: "Tessa NIA as-is",
      meaning: "use the existing NIA field directly · zero new derivation, fastest path",
    },
    {
      rule: "leasable",
      title: "different model — \"everything rentable\"",
      meaning: "NIA + commercial + non-residential · matches the English term \"GLA\"",
    },
  ];
  const ruleValues = useMemo(
    () =>
      RULE_DEFS.map((def) => ({
        ...def,
        value: computeGla(arch, def.rule, overrides).value,
      })),
    [arch, overrides]
  );

  return (
    <div className="my-1.5 border-l-2 border-warning-7 pl-2 py-1 bg-warning-1/40 rounded-r">
      {header}
      <p className="text-[10px] text-neutral-6 mt-1 font-mono leading-snug">
        = {calc.inputs.glaExpression}
      </p>
      <p className="text-[10px] text-neutral-7 mt-1.5 leading-snug">
        <strong>The spec says</strong> "WFL = GFA minus ancillary + circulation" — but
        Tessa has no <code>ancillary</code> or <code>circulation</code> field by name.
        The three rules below are pragmatic translations of that sentence into Tessa's
        vocabulary. Drives KG 300 + KG 400 (the two biggest cost lines).
      </p>
      <div className="text-[10px] mt-1.5 space-y-0.5">
        {ruleValues.map(({ rule, title, meaning, value }) => {
          const active = rule === wflOption;
          return (
            <div
              key={rule}
              className={`flex items-baseline gap-2 ${
                active ? "text-warning-7" : "text-neutral-6"
              }`}
            >
              <span className="font-mono shrink-0 w-3">{active ? "▸" : ""}</span>
              <code className={`shrink-0 ${active ? "font-semibold" : ""}`}>{rule}</code>
              <span className="shrink-0 text-neutral-5">— {title}</span>
              <span className="ml-auto font-mono shrink-0">{fmt(value)} m²</span>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-neutral-5 mt-1 leading-snug italic">
        Active formula: {ruleValues.find((r) => r.rule === wflOption)?.meaning}
      </p>
      <p className="text-[10px] text-danger-7 mt-1.5 leading-snug">
        ⚠ <strong>None of these implement WoFlV</strong> (the actual German legal
        definition — counts balconies at 25%, ceiling-height rules, etc.). Validate the
        rule choice with a German QS before quantitative use, or switch Mode → Manual to
        type in a real value.
      </p>
    </div>
  );
}

function GeometryRow(props: {
  label: string;
  german: string;
  value: string;
  source: string;
  tooltip: string;
  derived?: boolean;
}) {
  const valueClass = props.derived ? "text-warning-7" : "text-neutral-8";
  return (
    <div className="flex items-baseline gap-2 py-0.5 text-[11px]">
      <Tooltip.Trigger text={props.tooltip} className="shrink-0">
        <span className="text-neutral-7">
          {props.label} <span className="text-neutral-5">({props.german})</span>
        </span>
      </Tooltip.Trigger>
      <span className={`ml-auto font-mono ${valueClass}`}>{props.value}</span>
      <SourceBadge source={props.source} />
    </div>
  );
}

function RateRow(props: {
  kgKey: keyof typeof GERMAN_DEFAULTS;
  label: string;
  unit: string;
  isPct?: boolean;
  resolved: Resolved<number | null>;
  mode: EditMode;
  overrides: Record<string, number>;
  setOverride: (key: string, value: number | null) => void;
  tooltip: string;
}) {
  const { resolved, mode, kgKey, isPct, overrides, setOverride } = props;
  const isMissing = resolved.value == null;
  const isManual = mode === "manual";

  // Display value in % or absolute. cleanFloat strips IEEE-754 noise from the
  // /100 → ×100 round-trip (e.g. 0.055 × 100 = 5.500000000000001 → 5.5).
  const displayValue = isPct && resolved.value != null ? cleanFloat(resolved.value * 100) : resolved.value;
  const overrideDisplay =
    isPct && overrides[kgKey] != null ? cleanFloat(overrides[kgKey] * 100) : overrides[kgKey];

  return (
    <div className="flex items-baseline gap-2 py-0.5 text-[11px]">
      <Tooltip.Trigger text={props.tooltip} className="shrink-0">
        <span className={`${isMissing ? "text-danger-7" : "text-neutral-7"}`}>{props.label}</span>
      </Tooltip.Trigger>
      <div className="ml-auto flex items-center gap-2">
        {isManual ? (
          <input
            type="number"
            step="any"
            aria-label={`${props.label} override (${props.unit})`}
            placeholder={displayValue != null ? String(round2(displayValue)) : "—"}
            value={overrideDisplay != null ? String(overrideDisplay) : ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") return setOverride(kgKey, null);
              const n = Number(raw);
              if (Number.isNaN(n)) return;
              setOverride(kgKey, isPct ? n / 100 : n);
            }}
            className={`w-24 px-1 py-0.5 border rounded font-mono text-right ${
              isMissing ? "border-danger-7 bg-danger-1" : "border-neutral-3"
            }`}
          />
        ) : (
          <span className={`font-mono ${isMissing ? "text-danger-7" : "text-neutral-8"}`}>
            {isMissing ? "—" : `${round2(displayValue!)}`}
          </span>
        )}
        <span className="text-neutral-5">{props.unit}</span>
        <SourceBadge source={resolved.source} />
      </div>
    </div>
  );
}

// ─── Equations column ─────────────────────────────────────────────────────

function EquationsColumn(props: {
  system: System;
  view: ViewMode;
  ukSummary: CostingViewSummary | null;
  germanCalc: GermanCalc | null;
}) {
  const ukSection = (
    <Section blurred={props.system !== "uk"} label="UK">
      {props.ukSummary ? <UkEquations summary={props.ukSummary} view={props.view} /> : <Skel />}
    </Section>
  );
  const germanSection = (
    <Section blurred={props.system !== "german"} label="German">
      {props.germanCalc ? <GermanEquations calc={props.germanCalc} view={props.view} /> : <Skel />}
    </Section>
  );
  return (
    <Column title="Equations" subtitle={props.system === "uk" ? "Cascading on-costs" : "Flat % × Σ(KG 200..500)"}>
      {props.system === "uk" ? (
        <>
          {ukSection}
          {germanSection}
        </>
      ) : (
        <>
          {germanSection}
          {ukSection}
        </>
      )}
    </Column>
  );
}

function UkEquations(props: { summary: CostingViewSummary; view: ViewMode }) {
  const s = props.summary;
  const dev = props.view === "dev";
  const rows: Array<[string, string]> = [
    [
      "Construction",
      dev
        ? `modules + fitOut + baseBuild = ${fmt(s.constructionModules)} + ${fmt(
            s.constructionFitOut
          )} + ${fmt(s.constructionBaseBuild)}`
        : "modules + fitOut + baseBuild",
    ],
    [
      "Preliminaries",
      dev ? `10% × construction = 0.10 × ${fmt(s.construction)}` : "10% × construction",
    ],
    [
      "Contingencies",
      dev
        ? `3% × (construction + prelims) = 0.03 × ${fmt(s.construction + s.preliminaries)}`
        : "3% × (construction + prelims)  · cascading",
    ],
    [
      "OHP",
      dev
        ? `5.5% × (construction + prelims + contingencies) = 0.055 × ${fmt(
            s.construction + s.preliminaries + s.contingencies
          )}`
        : "5.5% × (… + contingencies)  · cascading",
    ],
    ["Total", "Σ all above"],
  ];
  return <EqTable rows={rows} />;
}

function GermanEquations(props: { calc: GermanCalc; view: ViewMode }) {
  const { calc } = props;
  const dev = props.view === "dev";
  const rows: Array<{ label: string; formula: string; missing: string[] }> = [
    { label: "KG 100", formula: dev ? calc.kg.kg100.formula : "Plotsize × rate₁₀₀", missing: calc.kg.kg100.missing },
    { label: "KG 200", formula: dev ? calc.kg.kg200.formula : "Plotsize × rate₂₀₀", missing: calc.kg.kg200.missing },
    { label: "KG 300", formula: dev ? calc.kg.kg300.formula : "Ref area × rate₃₀₀", missing: calc.kg.kg300.missing },
    { label: "KG 400", formula: dev ? calc.kg.kg400.formula : "Ref area × rate₄₀₀", missing: calc.kg.kg400.missing },
    {
      label: "KG 500",
      formula: dev ? calc.kg.kg500.formula : "Plotsize × (1 − SCR) × rate₅₀₀",
      missing: calc.kg.kg500.missing,
    },
    { label: "KG 600", formula: dev ? calc.kg.kg600.formula : "lump₆₀₀", missing: calc.kg.kg600.missing },
    {
      label: "KG 700",
      formula: dev ? calc.kg.kg700.formula : "%₇₀₀ × Σ(KG 200..500)",
      missing: calc.kg.kg700.missing,
    },
    { label: "KG 800", formula: dev ? calc.kg.kg800.formula : "quote₈₀₀", missing: calc.kg.kg800.missing },
    {
      label: "Contingencies",
      formula: dev ? calc.kg.contingencies.formula : "%cont × Σ(KG 200..500)",
      missing: calc.kg.contingencies.missing,
    },
    {
      label: "OHP",
      formula: dev ? calc.kg.ohp.formula : "%ohp × Σ(KG 200..500)",
      missing: calc.kg.ohp.missing,
    },
    { label: "GIK", formula: "Σ(KG 100..800) + Contingencies + OHP", missing: [] },
  ];
  return (
    <div className="space-y-1">
      {rows.map((r) => {
        const blocked = r.missing.length > 0;
        return (
          <div key={r.label} className="flex items-baseline gap-2 text-[11px]">
            <span
              className={`shrink-0 w-24 ${blocked ? "text-danger-7" : "text-neutral-7"}`}
            >
              {r.label}
            </span>
            <span
              className={`font-mono ${
                blocked ? "text-danger-7 line-through decoration-danger-7/50" : "text-neutral-8"
              }`}
            >
              {r.formula}
            </span>
            {blocked && (
              <span className="text-danger-7 text-[10px] ml-auto">
                ⚠ {r.missing.join(" + ")} missing
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Outputs column ───────────────────────────────────────────────────────

function OutputsColumn(props: {
  system: System;
  ukSummary: CostingViewSummary | null;
  ukCurrency: string;
  germanCalc: GermanCalc | null;
}) {
  const ukSection = (
    <Section blurred={props.system !== "uk"} label="UK">
      {props.ukSummary ? (
        <UkOutputs summary={props.ukSummary} currency={props.ukCurrency} />
      ) : (
        <Skel />
      )}
    </Section>
  );
  const germanSection = (
    <Section blurred={props.system !== "german"} label="German">
      {props.germanCalc ? <GermanOutputs calc={props.germanCalc} /> : <Skel />}
    </Section>
  );
  return (
    <Column title="Outputs" subtitle={props.system === "uk" ? "Construction + on-costs" : "GIK = Σ(KG 100..800) + on-costs"}>
      {props.system === "uk" ? (
        <>
          {ukSection}
          {germanSection}
        </>
      ) : (
        <>
          {germanSection}
          {ukSection}
        </>
      )}
    </Column>
  );
}

function UkOutputs(props: { summary: CostingViewSummary; currency: string }) {
  const s = props.summary;
  const f = (n: number) => fmtCost(n, props.currency);
  return (
    <div className="space-y-0.5">
      <KvLine k="Construction" v={f(s.construction)} />
      <KvLine k="· Modules" v={f(s.constructionModules)} muted />
      <KvLine k="· Base build" v={f(s.constructionBaseBuild)} muted />
      <KvLine k="· Fit out" v={f(s.constructionFitOut)} muted />
      <KvLine k="Preliminaries" v={f(s.preliminaries)} />
      <KvLine k="Contingencies" v={f(s.contingencies)} />
      <KvLine k="OHP" v={f(s.overheadsAndProfits)} />
      <div className="border-t border-neutral-3 pt-1 mt-1">
        <KvLine k="Total" v={f(s.total)} bold />
      </div>
    </div>
  );
}

function GermanOutputs(props: { calc: GermanCalc }) {
  const { calc } = props;
  const f = (n: number | null) => (n == null ? "—" : fmtCost(n, "€"));
  const rows: Array<[string, KgResult, string]> = [
    ["KG 100 · Site", calc.kg.kg100, calc.rates.rate100.source],
    ["KG 200 · Clearance", calc.kg.kg200, calc.rates.rate200.source],
    ["KG 300 · Construction", calc.kg.kg300, calc.rates.rate300.source],
    ["KG 400 · Technical", calc.kg.kg400, calc.rates.rate400.source],
    ["KG 500 · Landscaping", calc.kg.kg500, calc.rates.rate500.source],
    ["KG 600 · Furnishing", calc.kg.kg600, calc.rates.lump600.source],
    ["KG 700 · Incidentals", calc.kg.kg700, calc.rates.pct700.source],
    ["KG 800 · Financing", calc.kg.kg800, calc.rates.quote800.source],
    ["Contingencies", calc.kg.contingencies, calc.rates.pctContingency.source],
    ["OHP", calc.kg.ohp, calc.rates.pctOhp.source],
  ];
  return (
    <div className="space-y-0.5">
      {rows.map(([label, kg, source]) => {
        const blocked = kg.value == null;
        return (
          <div key={label} className="flex items-baseline gap-2 text-[11px]">
            <span className={`${blocked ? "text-danger-7" : "text-neutral-7"}`}>{label}</span>
            <span
              className={`ml-auto font-mono ${blocked ? "text-danger-7" : "text-neutral-8"}`}
            >
              {f(kg.value)}
            </span>
            <SourceBadge source={source} />
          </div>
        );
      })}
      <div className="border-t border-neutral-3 pt-1 mt-1 flex items-baseline gap-2 text-[11px]">
        <span className="font-bold">{calc.isPartial ? "GIK · partial" : "GIK · total"}</span>
        <span
          className={`ml-auto font-mono font-bold ${
            calc.isPartial ? "text-danger-7" : "text-neutral-8"
          }`}
        >
          {fmtCost(calc.total ?? calc.partialTotal, "€")}
        </span>
      </div>
      {calc.isPartial && (
        <p className="text-[10px] text-danger-7 italic mt-1 leading-snug">
          Partial sum of {10 - calc.missingKgLabels.length} of 10 lines. Excludes:{" "}
          {calc.missingKgLabels.join(", ")}. Real GIK will be higher once those rates are
          provided.
        </p>
      )}
    </div>
  );
}

// ─── Shared components ────────────────────────────────────────────────────

function Column(props: { title: string; subtitle: string; children: ReactNode }) {
  return (
    <section className="border border-neutral-3 rounded bg-white">
      <div className="px-3 py-2 border-b border-neutral-3 bg-neutral-1">
        <h2 className="text-[11px] font-bold uppercase tracking-wide">{props.title}</h2>
        <p className="text-[10px] text-neutral-6">{props.subtitle}</p>
      </div>
      <div className="p-3 space-y-3">{props.children}</div>
    </section>
  );
}

function Section(props: { blurred: boolean; label: string; children: ReactNode }) {
  return (
    <div className={`transition-opacity ${props.blurred ? "opacity-30 pointer-events-none" : ""}`}>
      <p className="text-[10px] uppercase tracking-wide text-neutral-6 mb-1">{props.label}</p>
      {props.children}
    </div>
  );
}

function SectionLabel(props: { children: ReactNode }) {
  return (
    <p className="text-[10px] uppercase tracking-wide text-neutral-6 mb-1 mt-2">{props.children}</p>
  );
}

function SourceBadge(props: { source: string }) {
  const s = props.source;
  const tone = s.startsWith("MANUAL")
    ? "bg-warning-2 text-warning-8"
    : s.startsWith("DERIVED")
    ? "bg-warning-1 text-warning-7"
    : s === "DEFAULT"
    ? "bg-neutral-2 text-neutral-7"
    : s === "BKI" || s === "BRW" || s === "API"
    ? "bg-danger-1 text-danger-7"
    : s === "USER"
    ? "bg-neutral-2 text-neutral-7"
    : s.startsWith("HAVE")
    ? "bg-success-1 text-success-7"
    : "bg-neutral-2 text-neutral-6";
  return (
    <span className={`px-1 py-[1px] rounded text-[9px] uppercase tracking-wider ${tone}`}>
      {s}
    </span>
  );
}

function KvLine(props: { k: string; v: string; muted?: boolean; bold?: boolean }) {
  return (
    <div
      className={`flex items-baseline gap-2 text-[11px] ${
        props.muted ? "text-neutral-6" : "text-neutral-8"
      } ${props.bold ? "font-bold" : ""}`}
    >
      <span>{props.k}</span>
      <span className="ml-auto font-mono">{props.v}</span>
    </div>
  );
}

function KvTable(props: { rows: Array<[string, string]> }) {
  return (
    <div className="space-y-[1px]">
      {props.rows.map(([k, v]) => (
        <div key={k} className="flex items-baseline gap-2 text-[10px]">
          <span className="text-neutral-6 shrink-0">{k}</span>
          <span className="ml-auto font-mono text-neutral-8">{v}</span>
        </div>
      ))}
    </div>
  );
}

function EqTable(props: { rows: Array<[string, string]> }) {
  return (
    <div className="space-y-1">
      {props.rows.map(([k, v]) => (
        <div key={k} className="flex items-baseline gap-2 text-[11px]">
          <span className="shrink-0 w-24 text-neutral-7">{k}</span>
          <span className="font-mono text-neutral-8">{v}</span>
        </div>
      ))}
    </div>
  );
}

function DevJson(props: { value: unknown }) {
  return (
    <pre className="text-[10px] text-neutral-7 overflow-x-auto bg-neutral-1 p-2 rounded">
      {JSON.stringify(props.value, null, 2)}
    </pre>
  );
}

function Skel() {
  return <p className="text-neutral-5 italic text-[11px]">Generating…</p>;
}

// ─── Screen-only formatters (fmt / fmtRate / pct come from engine module) ─

function fmtCost(n: number, currency: string): string {
  return `${currency}${Math.round(n).toLocaleString("en-GB")}`;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// Strips IEEE-754 noise without losing legitimate user precision.
function cleanFloat(n: number): number {
  return Math.round(n * 1e10) / 1e10;
}
